import React, { ChangeEvent, FC, KeyboardEvent, PointerEvent, useRef, useState } from 'react';
import { BasicButton, GhostButton, PrimaryButton } from 'src/components/widgets/buttons';
import { QuestionDialog, useDialogManager } from 'src/components/widgets/dialog';
import Stack from 'src/components/widgets/stack/stack';
import { T, uploadFile } from 'src/shared/helpers';
import styled from 'styled-components';
import { AgentProductPreviewActions, ProductArea, ProductSide } from './types';
import {
    AreaRectangle,
    ImagePoint,
    ImageSize,
    ResizeHandle,
    clampValue,
    drawRectangle,
    isValidRectangle,
    moveRectangle,
    resizeRectangle,
    setRectangleSize,
    snapRulerPoint
} from './printAreaGeometry';

const MAX_IMAGE_BYTES = 12 * 1024 * 1024;
const HANDLES: Array<{ key: ResizeHandle; x: number; y: number; label: string }> = [
    { key: 'nw', x: 0, y: 0, label: T._('Resize from top left', 'Admin') },
    { key: 'n', x: 0.5, y: 0, label: T._('Resize from top', 'Admin') },
    { key: 'ne', x: 1, y: 0, label: T._('Resize from top right', 'Admin') },
    { key: 'e', x: 1, y: 0.5, label: T._('Resize from right', 'Admin') },
    { key: 'se', x: 1, y: 1, label: T._('Resize from bottom right', 'Admin') },
    { key: 's', x: 0.5, y: 1, label: T._('Resize from bottom', 'Admin') },
    { key: 'sw', x: 0, y: 1, label: T._('Resize from bottom left', 'Admin') },
    { key: 'w', x: 0, y: 0.5, label: T._('Resize from left', 'Admin') }
];

interface AgentProductSidePreviewProps extends AgentProductPreviewActions {
    variantRef: string;
    side: ProductSide;
    revisionNumber: number | null;
    disabled?: boolean;
}
interface AreaEdit {
    areaRef: string | null;
    rect: AreaRectangle;
    name: string;
    revision: number;
    unit: 'cm' | 'px';
    ratio: number | null;
}
interface Drag {
    mode: 'draw' | 'move' | ResizeHandle;
    start: ImagePoint;
    original: AreaRectangle;
    pointerId: number;
}
interface ScaleEdit {
    value: string;
    revision: number;
    mode: 'measure' | 'manual';
    reference: { start: ImagePoint; end: ImagePoint } | null;
}
const RulerOverlay = styled.svg`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
    pointer-events: none;
    z-index: 4;
`;

const Card = styled(Stack)`
    min-width: 0;
    padding: 14px;
    border: 1px solid #dfe6eb;
    border-radius: 12px;
    background: white;
`;
const Heading = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    color: #263746;
    font-size: 14px;
    font-weight: 600;
`;
const Counter = styled.span`
    color: #657580;
    font-size: 11px;
    font-weight: 400;
`;
const Stage = styled.div`
    position: relative;
    display: flex;
    min-height: 150px;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 14px;
    border-radius: 8px;
    background: #f2f5f7;
    user-select: none;
`;
const ImageFrame = styled.div<{ $aspectRatio: number; $drawing: boolean }>`
    position: relative;
    width: min(100%, ${(props) => 400 * props.$aspectRatio}px);
    max-height: 400px;
    aspect-ratio: ${(props) => props.$aspectRatio};
    cursor: ${(props) => (props.$drawing ? 'crosshair' : 'default')};
    touch-action: none;
    > img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
        pointer-events: none;
    }
`;
const Rectangle = styled.div<{ $rect: AreaRectangle; $image: ImageSize; $active?: boolean; $selected?: boolean; $interactive: boolean }>`
    position: absolute;
    box-sizing: border-box;
    left: ${(props) => (props.$rect.x / props.$image.width) * 100}%;
    top: ${(props) => (props.$rect.y / props.$image.height) * 100}%;
    width: ${(props) => (props.$rect.width / props.$image.width) * 100}%;
    height: ${(props) => (props.$rect.height / props.$image.height) * 100}%;
    border: 2px ${(props) => (props.$active || props.$selected ? 'solid' : 'dashed')} ${(props) => (props.$active ? '#d96b16' : '#00899d')};
    background: ${(props) => (props.$active ? 'rgba(217, 107, 22, 0.12)' : 'rgba(0, 137, 157, 0.06)')};
    z-index: ${(props) => (props.$active ? 3 : props.$selected ? 2 : 1)};
    cursor: ${(props) => (props.$active ? 'move' : 'pointer')};
    pointer-events: ${(props) => (props.$interactive ? 'auto' : 'none')};
    &:focus-visible {
        outline: 3px solid #1769aa;
        outline-offset: 3px;
    }
`;
const Handle = styled.button<{ $x: number; $y: number; $handle: ResizeHandle }>`
    position: absolute;
    left: ${(props) => props.$x * 100}%;
    top: ${(props) => props.$y * 100}%;
    width: 22px;
    height: 22px;
    padding: 0;
    border: 0;
    background: transparent;
    transform: translate(-50%, -50%);
    cursor: ${(props) => props.$handle}-resize;
    touch-action: none;
    &::after {
        content: '';
        position: absolute;
        inset: 6px;
        border: 2px solid #d96b16;
        border-radius: 2px;
        background: white;
    }
    &:focus-visible {
        outline: 2px solid #1769aa;
        border-radius: 3px;
    }
`;
const AreaLabel = styled.span`
    position: absolute;
    left: -2px;
    top: -2px;
    max-width: 170px;
    padding: 2px 5px;
    transform: translateY(-100%);
    background: #00899d;
    color: white;
    border-radius: 3px 3px 0 0;
    font-size: 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    pointer-events: none;
`;
const Row = styled(Stack)`
    flex-wrap: wrap;
    align-items: center;
`;
const FieldGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
`;
const Field = styled.label`
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
    color: #455967;
    font-size: 11px;
`;
const TextInput = styled.input`
    width: 100%;
    min-width: 0;
    height: 34px;
    box-sizing: border-box;
    padding: 0 9px;
    border: 1px solid #bac8d2;
    border-radius: 6px;
    background: white;
    color: #263746;
    font-size: 12px;
    &:focus-visible {
        outline: 2px solid #00899d;
        outline-offset: 1px;
    }
    &:disabled {
        background: #f2f5f7;
        color: #89969e;
    }
`;
const Select = styled.select`
    width: 100%;
    min-width: 0;
    height: 34px;
    padding: 0 8px;
    border: 1px solid #bac8d2;
    border-radius: 6px;
    background: white;
    color: #263746;
    font-size: 12px;
`;
const Hint = styled.p`
    margin: 0;
    color: #647783;
    font-size: 11px;
    line-height: 1.5;
`;
const ErrorText = styled(Hint)`
    color: #b42318;
`;
const Notice = styled(Stack)`
    padding: 10px;
    border-radius: 7px;
    background: #edf7f4;
    color: #276653;
    font-size: 12px;
`;
const Inspector = styled(Stack)`
    padding: 12px;
    border: 1px solid #e0e7eb;
    border-radius: 8px;
    background: #fafcfd;
`;
const HiddenFileInput = styled.input`
    display: none;
`;

const numberText = (value: number, precision = 3) => Number(value.toFixed(precision)).toString();
const parseNumber = (value: string) => (value.trim() ? Number(value.replace(',', '.')) : NaN);
const areaName = (area: ProductArea, index: number) => area.name || area.label || `${T._('Print area', 'Admin')} ${index + 1}`;
const rectangleOf = (area: ProductArea | null, image: ImageSize): AreaRectangle | null => {
    const raw = area?.bounds?.raw;
    if (!raw || ![raw.x, raw.y, raw.width, raw.height].every(Number.isFinite) || raw.width <= 0 || raw.height <= 0) return null;
    return { x: raw.x + image.width / 2, y: raw.y + image.height / 2, width: raw.width, height: raw.height };
};
const readImageDimensions = (file: File) =>
    new Promise<{ widthPx: number; heightPx: number }>((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve({ widthPx: image.naturalWidth, heightPx: image.naturalHeight });
        };
        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error(T._('The selected image could not be read.', 'Admin')));
        };
        image.src = objectUrl;
    });

export const AgentProductSidePreview: FC<AgentProductSidePreviewProps> = ({
    variantRef,
    side,
    revisionNumber,
    disabled,
    onUpsertPrintArea,
    onDeletePrintArea,
    onRestorePrintArea,
    onSetSideScale,
    onReplaceSideImage
}) => {
    const stageRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dragRef = useRef<Drag | null>(null);
    const rulerDragRef = useRef<{ pointerId: number; start: ImagePoint; previous: ScaleEdit['reference'] } | null>(null);
    const savingRef = useRef(false);
    const { showDialog, closeDialog } = useDialogManager();
    const areas = side.areas ?? [];
    const image = side.image;
    const imageSize = { width: image?.widthPx ?? 0, height: image?.heightPx ?? 0 };
    const scale = side.resolution?.pixelsPerCm;
    const hasScale = Boolean(scale && Number.isFinite(scale) && scale > 0);
    const [selectedRef, setSelectedRef] = useState<string | null>(areas[0]?.entityReferenceKey ?? null);
    const selectedArea = areas.find((area) => area.entityReferenceKey === selectedRef) ?? areas[0] ?? null;
    const [edit, setEdit] = useState<AreaEdit | null>(null);
    const [widthText, setWidthText] = useState('');
    const [heightText, setHeightText] = useState('');
    const [geometryError, setGeometryError] = useState<string | null>(null);
    const [localError, setLocalError] = useState<string | null>(null);
    const [working, setWorking] = useState(false);
    const [scaleEdit, setScaleEdit] = useState<ScaleEdit | null>(null);
    const [undo, setUndo] = useState<{ areaRef: string; name: string; revision: number } | null>(null);
    const busy = Boolean(disabled || working);
    const canEdit = Boolean(variantRef && side.entityReferenceKey && revisionNumber && image?.url && imageSize.width > 0 && imageSize.height > 0);
    const staleEdit = Boolean(edit && edit.revision !== revisionNumber);
    const staleScale = Boolean(scaleEdit && scaleEdit.revision !== revisionNumber);
    const canManipulate = canEdit && !busy && !staleEdit && !scaleEdit;
    const factor = edit?.unit === 'cm' && hasScale ? scale! : 1;
    const referenceLength = scaleEdit?.reference
        ? Math.hypot(scaleEdit.reference.end.x - scaleEdit.reference.start.x, scaleEdit.reference.end.y - scaleEdit.reference.start.y)
        : 0;
    const scaleValue =
        scaleEdit?.mode === 'measure'
            ? referenceLength >= 1 && parseNumber(scaleEdit.value) > 0
                ? referenceLength / parseNumber(scaleEdit.value)
                : NaN
            : parseNumber(scaleEdit?.value ?? '');
    const validScale = Number.isFinite(scaleValue) && scaleValue >= 0.0001;
    const canMeasure = canEdit && !busy && !staleScale && scaleEdit?.mode === 'measure';

    const syncDimensions = (rect: AreaRectangle, unit: 'cm' | 'px') => {
        const divisor = unit === 'cm' && hasScale ? scale! : 1;
        setWidthText(numberText(rect.width / divisor));
        setHeightText(numberText(rect.height / divisor));
        setGeometryError(null);
    };
    const updateRectangle = (rect: AreaRectangle) => {
        if (!edit) return;
        setEdit({ ...edit, rect });
        syncDimensions(rect, edit.unit);
    };
    const startEditing = (area: ProductArea | null) => {
        if (!canEdit || busy || scaleEdit || (area && !area.entityReferenceKey)) return;
        const rect = rectangleOf(area, imageSize) ?? {
            x: imageSize.width * 0.3,
            y: imageSize.height * 0.3,
            width: imageSize.width * 0.4,
            height: imageSize.height * 0.4
        };
        const unit = hasScale ? 'cm' : 'px';
        setEdit({
            areaRef: area?.entityReferenceKey ?? null,
            rect,
            name: area ? area.name || area.label || '' : `${T._('Print area', 'Admin')} ${areas.length + 1}`,
            revision: revisionNumber!,
            unit,
            ratio: null
        });
        syncDimensions(rect, unit);
        setLocalError(null);
        if (area?.entityReferenceKey) setSelectedRef(area.entityReferenceKey);
    };
    const stopEditing = () => {
        dragRef.current = null;
        setEdit(null);
        setGeometryError(null);
        setLocalError(null);
    };
    const eventPoint = (event: PointerEvent): ImagePoint => {
        const box = stageRef.current!.getBoundingClientRect();
        return {
            x: clampValue((event.clientX - box.left) / box.width, 0, 1) * imageSize.width,
            y: clampValue((event.clientY - box.top) / box.height, 0, 1) * imageSize.height
        };
    };
    const beginDrag = (event: PointerEvent, mode: Drag['mode'], rect: AreaRectangle) => {
        if (!canManipulate || event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        dragRef.current = { mode, original: rect, start: eventPoint(event), pointerId: event.pointerId };
        stageRef.current?.setPointerCapture(event.pointerId);
    };
    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const ruler = rulerDragRef.current;
        if (ruler && ruler.pointerId === event.pointerId && canMeasure && scaleEdit) {
            setScaleEdit({ ...scaleEdit, reference: { start: ruler.start, end: snapRulerPoint(ruler.start, eventPoint(event)) } });
            return;
        }
        const drag = dragRef.current;
        if (!drag || !edit || !canManipulate || event.pointerId !== drag.pointerId) return;
        const point = eventPoint(event);
        const dx = point.x - drag.start.x;
        const dy = point.y - drag.start.y;
        updateRectangle(
            drag.mode === 'draw'
                ? drawRectangle(drag.start, point, imageSize, edit.ratio)
                : drag.mode === 'move'
                  ? moveRectangle(drag.original, dx, dy, imageSize)
                  : resizeRectangle(drag.original, drag.mode, dx, dy, imageSize, edit.ratio)
        );
    };
    const finishDrag = (event: PointerEvent<HTMLDivElement>, cancel = false) => {
        const ruler = rulerDragRef.current;
        if (ruler && ruler.pointerId === event.pointerId) {
            if (canMeasure && scaleEdit)
                setScaleEdit({
                    ...scaleEdit,
                    reference: cancel ? ruler.previous : { start: ruler.start, end: snapRulerPoint(ruler.start, eventPoint(event)) }
                });
            rulerDragRef.current = null;
            if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
            return;
        }
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        if (cancel) updateRectangle(drag.original);
        dragRef.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    };
    const onGeometryKey = (event: KeyboardEvent, handle?: ResizeHandle) => {
        if (!edit || !canManipulate) return;
        if (event.key === 'Escape') {
            event.preventDefault();
            stopEditing();
            return;
        }
        const delta = event.shiftKey ? 10 : 1;
        const dx = event.key === 'ArrowLeft' ? -delta : event.key === 'ArrowRight' ? delta : 0;
        const dy = event.key === 'ArrowUp' ? -delta : event.key === 'ArrowDown' ? delta : 0;
        if (!dx && !dy) return;
        event.preventDefault();
        event.stopPropagation();
        updateRectangle(handle ? resizeRectangle(edit.rect, handle, dx, dy, imageSize, edit.ratio) : moveRectangle(edit.rect, dx, dy, imageSize));
    };
    const changeDimension = (axis: 'width' | 'height', text: string) => {
        if (!edit) return;
        let nextWidth = axis === 'width' ? text : widthText;
        let nextHeight = axis === 'height' ? text : heightText;
        if (edit.ratio && Number.isFinite(parseNumber(text))) {
            if (axis === 'width') nextHeight = numberText(parseNumber(text) / edit.ratio, 6);
            else nextWidth = numberText(parseNumber(text) * edit.ratio, 6);
        }
        setWidthText(nextWidth);
        setHeightText(nextHeight);
        const rect = setRectangleSize(edit.rect, parseNumber(nextWidth) * factor, parseNumber(nextHeight) * factor, imageSize);
        if (!rect) {
            setGeometryError(T._('Enter positive dimensions that fit within the side image (at least 1 pixel).', 'Admin'));
            return;
        }
        setGeometryError(null);
        setEdit({ ...edit, rect });
    };
    const changeRatio = (value: string) => {
        if (!edit) return;
        const ratio = value === 'free' ? null : value === 'current' ? edit.rect.width / edit.rect.height : Number(value);
        let rect = edit.rect;
        if (ratio) {
            const width = Math.min(rect.width, imageSize.height * ratio);
            const resized = setRectangleSize(rect, width, width / ratio, imageSize);
            if (!resized) return;
            rect = resized;
        }
        setEdit({ ...edit, rect, ratio });
        syncDimensions(rect, edit.unit);
    };
    const perform = async (action: () => Promise<void>) => {
        if (savingRef.current || busy) return;
        savingRef.current = true;
        setWorking(true);
        setLocalError(null);
        try {
            await action();
        } catch (error) {
            setLocalError((error as Error).message || T._('The draft could not be updated.', 'Admin'));
        } finally {
            savingRef.current = false;
            setWorking(false);
        }
    };
    const saveArea = () =>
        perform(async () => {
            if (!edit || staleEdit || geometryError || !isValidRectangle(edit.rect, imageSize)) return;
            const result = await onUpsertPrintArea({
                variantRef,
                sideRef: side.entityReferenceKey!,
                areaRef: edit.areaRef,
                expectedRevisionNumber: edit.revision,
                name: edit.name.trim(),
                x1: edit.rect.x,
                y1: edit.rect.y,
                x2: edit.rect.x + edit.rect.width,
                y2: edit.rect.y + edit.rect.height
            });
            if (result) {
                setSelectedRef(result.areaRef ?? null);
                setUndo(null);
                stopEditing();
            } else setLocalError(T._('The area was not saved. Check the conversation message and try again.', 'Admin'));
        });
    const deleteArea = () =>
        perform(async () => {
            if (!selectedArea?.entityReferenceKey || !revisionNumber) return;
            const result = await onDeletePrintArea({
                variantRef,
                sideRef: side.entityReferenceKey!,
                areaRef: selectedArea.entityReferenceKey,
                expectedRevisionNumber: revisionNumber
            });
            if (result) {
                setUndo({
                    areaRef: selectedArea.entityReferenceKey,
                    name: areaName(selectedArea, areas.indexOf(selectedArea)),
                    revision: result.revisionNumber
                });
                setSelectedRef(null);
            } else setLocalError(T._('The area could not be deleted.', 'Admin'));
        });
    const restoreArea = () =>
        perform(async () => {
            if (!undo || undo.revision !== revisionNumber) return;
            const result = await onRestorePrintArea({
                variantRef,
                sideRef: side.entityReferenceKey!,
                areaRef: undo.areaRef,
                expectedRevisionNumber: undo.revision
            });
            if (result) {
                setSelectedRef(result.areaRef ?? undo.areaRef);
                setUndo(null);
            } else setLocalError(T._('The deletion could not be undone.', 'Admin'));
        });
    const saveScale = () =>
        perform(async () => {
            if (!scaleEdit || staleScale) return;
            const pixelsPerCm = scaleValue;
            if (!Number.isFinite(pixelsPerCm) || pixelsPerCm < 0.0001) return;
            const result = await onSetSideScale({ variantRef, sideRef: side.entityReferenceKey!, pixelsPerCm, expectedRevisionNumber: scaleEdit.revision });
            if (result) {
                setScaleEdit(null);
                setUndo(null);
            } else setLocalError(T._('The scale could not be saved.', 'Admin'));
        });
    const replaceImage = (file: File) =>
        perform(async () => {
            closeDialog('agent-replace-side-image');
            const expectedRevisionNumber = revisionNumber!;
            const dimensions = await readImageDimensions(file);
            const url = await uploadFile(file);
            const result = await onReplaceSideImage({ variantRef, sideRef: side.entityReferenceKey!, expectedRevisionNumber, url, ...dimensions });
            if (result) {
                setSelectedRef(null);
                setUndo(null);
                stopEditing();
            } else setLocalError(T._('The image could not be replaced.', 'Admin'));
        });
    const onFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setLocalError(T._('Select a valid image file.', 'Admin'));
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            setLocalError(T._('Images must be smaller than 12 MB.', 'Admin'));
            return;
        }
        if (!hasScale && areas.length === 0) {
            void replaceImage(file);
            return;
        }
        showDialog(
            'agent-replace-side-image',
            <QuestionDialog
                title={T._('Replace side image', 'Admin')}
                message={T._(
                    'Print areas and scale are based on the current image. Replacing it will clear them for this side, so they can be configured again.',
                    'Admin'
                )}
                buttonYesLabel={T._('Replace image', 'Admin')}
                buttonNoLabel={T._('Cancel', 'Admin')}
                onYesClick={() => void replaceImage(file)}
                onNoClick={() => closeDialog('agent-replace-side-image')}
            />
        );
    };
    const sizeLabel = (rect: AreaRectangle) =>
        `${numberText(rect.width / (hasScale ? scale! : 1), 2)} × ${numberText(rect.height / (hasScale ? scale! : 1), 2)} ${hasScale ? 'cm' : 'px'}`;

    return (
        <Card $gap='12px'>
            <Heading>
                <span>{side.name || T._('Unnamed side', 'Admin')}</span>
                <Counter>
                    {areas.length} {T._('areas', 'Admin')}
                </Counter>
            </Heading>
            <Stage>
                {image?.url ? (
                    <ImageFrame
                        ref={stageRef}
                        $aspectRatio={imageSize.width && imageSize.height ? imageSize.width / imageSize.height : 1}
                        $drawing={Boolean(edit && !edit.areaRef) || Boolean(canMeasure)}
                        onPointerDown={(event) => {
                            if (canMeasure && scaleEdit && event.button === 0) {
                                event.preventDefault();
                                const start = eventPoint(event);
                                rulerDragRef.current = { pointerId: event.pointerId, start, previous: scaleEdit.reference };
                                setScaleEdit({ ...scaleEdit, reference: { start, end: start } });
                                event.currentTarget.setPointerCapture(event.pointerId);
                                return;
                            }
                            if (edit && !edit.areaRef) beginDrag(event, 'draw', edit.rect);
                        }}
                        onPointerMove={onPointerMove}
                        onPointerUp={(event) => finishDrag(event)}
                        onPointerCancel={(event) => finishDrag(event, true)}
                        onLostPointerCapture={() => {
                            dragRef.current = null;
                            rulerDragRef.current = null;
                        }}
                    >
                        <img src={image.url} alt={side.name || T._('Product side', 'Admin')} loading='lazy' draggable={false} />
                        {scaleEdit?.mode === 'measure' && scaleEdit.reference && (
                            <RulerOverlay viewBox={`0 0 ${imageSize.width} ${imageSize.height}`} role='img' aria-label={T._('Scale reference', 'Admin')}>
                                <line
                                    x1={scaleEdit.reference.start.x}
                                    y1={scaleEdit.reference.start.y}
                                    x2={scaleEdit.reference.end.x}
                                    y2={scaleEdit.reference.end.y}
                                    stroke='white'
                                    strokeWidth='5'
                                    vectorEffect='non-scaling-stroke'
                                />
                                <line
                                    x1={scaleEdit.reference.start.x}
                                    y1={scaleEdit.reference.start.y}
                                    x2={scaleEdit.reference.end.x}
                                    y2={scaleEdit.reference.end.y}
                                    stroke='#d96b16'
                                    strokeWidth='2'
                                    vectorEffect='non-scaling-stroke'
                                />
                                {[scaleEdit.reference.start, scaleEdit.reference.end].map((point, index) => (
                                    <circle
                                        key={index}
                                        cx={point.x}
                                        cy={point.y}
                                        r={imageSize.width / 100}
                                        fill='white'
                                        stroke='#d96b16'
                                        strokeWidth='2'
                                        vectorEffect='non-scaling-stroke'
                                    />
                                ))}
                            </RulerOverlay>
                        )}
                        {imageSize.width > 0 &&
                            imageSize.height > 0 &&
                            areas.map((area, index) => {
                                const rect = rectangleOf(area, imageSize);
                                if (!rect || (edit && area.entityReferenceKey === edit.areaRef)) return null;
                                const selected = selectedArea === area && !edit;
                                return (
                                    <Rectangle
                                        key={area.entityReferenceKey ?? index}
                                        $rect={rect}
                                        $image={imageSize}
                                        $selected={selected}
                                        $interactive={!edit && !scaleEdit && !busy}
                                        role='button'
                                        tabIndex={!edit && !scaleEdit && !busy ? 0 : -1}
                                        aria-label={`${areaName(area, index)} · ${sizeLabel(rect)}`}
                                        aria-pressed={selected}
                                        onPointerDown={(event) => {
                                            if (!canManipulate) return;
                                            startEditing(area);
                                            beginDrag(event, 'move', rect);
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                startEditing(area);
                                            }
                                        }}
                                    >
                                        <AreaLabel>
                                            {index + 1} · {areaName(area, index)}
                                        </AreaLabel>
                                    </Rectangle>
                                );
                            })}
                        {edit && (
                            <Rectangle
                                $rect={edit.rect}
                                $image={imageSize}
                                $active
                                $interactive={canManipulate}
                                role='group'
                                tabIndex={0}
                                aria-label={T._('Selected print area. Use arrow keys to move.', 'Admin')}
                                onPointerDown={(event) => beginDrag(event, 'move', edit.rect)}
                                onKeyDown={(event) => onGeometryKey(event)}
                            >
                                {HANDLES.map((handle) => (
                                    <Handle
                                        key={handle.key}
                                        type='button'
                                        $x={handle.x}
                                        $y={handle.y}
                                        $handle={handle.key}
                                        disabled={!canManipulate}
                                        aria-label={handle.label}
                                        onPointerDown={(event) => beginDrag(event, handle.key, edit.rect)}
                                        onKeyDown={(event) => onGeometryKey(event, handle.key)}
                                    />
                                ))}
                            </Rectangle>
                        )}
                    </ImageFrame>
                ) : (
                    <Hint>{T._('No image yet', 'Admin')}</Hint>
                )}
            </Stage>
            {edit ? (
                <Inspector $gap='10px'>
                    <Heading>
                        <span>{edit.areaRef ? T._('Edit area', 'Admin') : T._('New area', 'Admin')}</span>
                        <Counter>{sizeLabel(edit.rect)}</Counter>
                    </Heading>
                    <Hint>
                        {T._(
                            'Drag the area to move it. Drag the handles to resize it, or enter exact dimensions. Arrow keys move by 1 pixel; Shift moves by 10.',
                            'Admin'
                        )}
                    </Hint>
                    {!edit.areaRef && <Hint>{T._('You can also drag on the empty image to draw a new rectangle.', 'Admin')}</Hint>}
                    <Field>
                        {T._('Area name', 'Admin')}
                        <TextInput
                            value={edit.name}
                            maxLength={200}
                            disabled={busy || staleEdit}
                            onChange={(event) => setEdit({ ...edit, name: event.target.value })}
                        />
                    </Field>
                    <FieldGrid>
                        <Field>
                            {T._('Width', 'Admin')} ({edit.unit})
                            <TextInput
                                inputMode='decimal'
                                value={widthText}
                                disabled={busy || staleEdit}
                                aria-invalid={Boolean(geometryError)}
                                onChange={(event) => changeDimension('width', event.target.value)}
                            />
                        </Field>
                        <Field>
                            {T._('Height', 'Admin')} ({edit.unit})
                            <TextInput
                                inputMode='decimal'
                                value={heightText}
                                disabled={busy || staleEdit}
                                aria-invalid={Boolean(geometryError)}
                                onChange={(event) => changeDimension('height', event.target.value)}
                            />
                        </Field>
                        <Field>
                            {T._('Units', 'Admin')}
                            <Select
                                value={edit.unit}
                                disabled={busy || staleEdit}
                                onChange={(event) => {
                                    const unit = event.target.value as 'cm' | 'px';
                                    setEdit({ ...edit, unit });
                                    syncDimensions(edit.rect, unit);
                                }}
                            >
                                <option value='px'>px</option>
                                <option value='cm' disabled={!hasScale}>
                                    cm
                                </option>
                            </Select>
                        </Field>
                        <Field>
                            {T._('Proportions', 'Admin')}
                            <Select value={edit.ratio ? 'current' : 'free'} disabled={busy || staleEdit} onChange={(event) => changeRatio(event.target.value)}>
                                <option value='free'>{T._('Free', 'Admin')}</option>
                                <option value='current'>
                                    {edit.ratio ? `${T._('Locked', 'Admin')} · ${numberText(edit.ratio)}:1` : T._('Lock current ratio', 'Admin')}
                                </option>
                                <option value='1'>1:1</option>
                                <option value={4 / 3}>4:3</option>
                                <option value={3 / 4}>3:4</option>
                                <option value={16 / 9}>16:9</option>
                            </Select>
                        </Field>
                    </FieldGrid>
                    {!hasScale && <Hint>{T._('Set the side PPCM to use centimetres. You can already position the area in pixels.', 'Admin')}</Hint>}
                    {geometryError && <ErrorText role='alert'>{geometryError}</ErrorText>}
                    {staleEdit && (
                        <ErrorText role='alert'>
                            {T._('The draft changed while you were editing. Cancel and reopen the area to use the latest version.', 'Admin')}
                        </ErrorText>
                    )}
                    <Row $horizontal $gap='8px'>
                        <PrimaryButton
                            type='button'
                            $size='small'
                            disabled={busy || staleEdit || Boolean(geometryError) || !isValidRectangle(edit.rect, imageSize)}
                            onClick={() => void saveArea()}
                        >
                            {T._('Save area', 'Admin')}
                        </PrimaryButton>
                        <GhostButton type='button' $size='small' disabled={working} onClick={stopEditing}>
                            {T._('Cancel', 'Admin')}
                        </GhostButton>
                    </Row>
                </Inspector>
            ) : (
                <Stack $gap='10px'>
                    {areas.length > 0 && (
                        <Field>
                            {T._('Selected area', 'Admin')}
                            <Select
                                value={selectedArea?.entityReferenceKey ?? ''}
                                disabled={busy || Boolean(scaleEdit)}
                                onChange={(event) => setSelectedRef(event.target.value)}
                            >
                                {areas.map((area, index) => (
                                    <option key={area.entityReferenceKey ?? index} value={area.entityReferenceKey}>
                                        {index + 1} · {areaName(area, index)}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                    )}
                    {selectedArea && rectangleOf(selectedArea, imageSize) && <Hint>{sizeLabel(rectangleOf(selectedArea, imageSize)!)}</Hint>}
                    <Row $horizontal $gap='8px'>
                        <PrimaryButton type='button' $size='small' disabled={!canEdit || busy || Boolean(scaleEdit)} onClick={() => startEditing(null)}>
                            {T._('Add area', 'Admin')}
                        </PrimaryButton>
                        {selectedArea && (
                            <>
                                <BasicButton
                                    type='button'
                                    $size='small'
                                    disabled={!canEdit || busy || Boolean(scaleEdit) || !selectedArea.entityReferenceKey}
                                    onClick={() => startEditing(selectedArea)}
                                >
                                    {T._('Edit area', 'Admin')}
                                </BasicButton>
                                <GhostButton
                                    type='button'
                                    $size='small'
                                    disabled={!canEdit || busy || Boolean(scaleEdit) || !selectedArea.entityReferenceKey}
                                    onClick={() => void deleteArea()}
                                >
                                    {T._('Delete area', 'Admin')}
                                </GhostButton>
                            </>
                        )}
                    </Row>
                </Stack>
            )}
            {undo && undo.revision === revisionNumber && !edit && !scaleEdit && (
                <Notice $gap='6px' role='status'>
                    <span>
                        {undo.name} · {T._('Area deleted from the draft.', 'Admin')}
                    </span>
                    <Row $horizontal $gap='8px'>
                        <BasicButton type='button' $size='small' disabled={busy} onClick={() => void restoreArea()}>
                            {T._('Undo deletion', 'Admin')}
                        </BasicButton>
                    </Row>
                </Notice>
            )}
            <Inspector $gap='8px'>
                <Heading>
                    <span>{T._('Side scale', 'Admin')}</span>
                    <Counter>{hasScale ? `${numberText(scale!, 4)} PPCM` : T._('Not set', 'Admin')}</Counter>
                </Heading>
                {scaleEdit ? (
                    <>
                        <Hint>
                            {scaleEdit.mode === 'measure'
                                ? T._(
                                      'Drag horizontally or vertically on the image, then enter the real distance in centimetres. The ruler snaps to the nearest axis. Drag again to change the reference.',
                                      'Admin'
                                  )
                                : T._('Enter the number of image pixels per centimetre.', 'Admin')}
                        </Hint>
                        <Field>
                            {scaleEdit.mode === 'measure' ? T._('Reference distance (cm)', 'Admin') : T._('Pixels per centimetre (PPCM)', 'Admin')}
                            <TextInput
                                inputMode='decimal'
                                value={scaleEdit.value}
                                disabled={busy || staleScale}
                                onChange={(event) => setScaleEdit({ ...scaleEdit, value: event.target.value })}
                            />
                        </Field>
                        {scaleEdit.mode === 'measure' && (
                            <Hint role='status'>
                                {referenceLength >= 1
                                    ? `${T._('Reference', 'Admin')}: ${numberText(referenceLength, 2)} px${validScale ? ` · ${numberText(scaleValue, 4)} PPCM` : ''}`
                                    : T._('Draw a reference on the image to set the scale.', 'Admin')}
                            </Hint>
                        )}
                        <Hint>{T._('All areas stay in place on the photo. Only their dimensions in centimetres change.', 'Admin')}</Hint>
                        {validScale && selectedArea && rectangleOf(selectedArea, imageSize) && (
                            <Hint>
                                {T._('Selected area at this scale', 'Admin')}: {numberText(rectangleOf(selectedArea, imageSize)!.width / scaleValue, 2)} ×{' '}
                                {numberText(rectangleOf(selectedArea, imageSize)!.height / scaleValue, 2)} cm
                            </Hint>
                        )}
                        {staleScale && <ErrorText role='alert'>{T._('The draft changed. Cancel and reopen the scale editor.', 'Admin')}</ErrorText>}
                        <Row $horizontal $gap='8px'>
                            <PrimaryButton type='button' $size='small' disabled={busy || staleScale || !validScale} onClick={() => void saveScale()}>
                                {T._('Save scale', 'Admin')}
                            </PrimaryButton>
                            <GhostButton type='button' $size='small' disabled={working} onClick={() => setScaleEdit(null)}>
                                {T._('Cancel', 'Admin')}
                            </GhostButton>
                        </Row>
                        <GhostButton
                            type='button'
                            $size='small'
                            disabled={busy || staleScale}
                            onClick={() =>
                                setScaleEdit({ ...scaleEdit, mode: scaleEdit.mode === 'measure' ? 'manual' : 'measure', value: '', reference: null })
                            }
                        >
                            {scaleEdit.mode === 'measure' ? T._('Enter PPCM manually', 'Admin') : T._('Measure on image', 'Admin')}
                        </GhostButton>
                    </>
                ) : (
                    <>
                        <Hint>{T._('PPCM is the number of image pixels in one real centimetre.', 'Admin')}</Hint>
                        <Row $horizontal $gap='8px'>
                            <BasicButton
                                type='button'
                                $size='small'
                                disabled={!canEdit || busy || Boolean(edit)}
                                onClick={() => {
                                    setScaleEdit({ value: '', revision: revisionNumber!, mode: 'measure', reference: null });
                                    setLocalError(null);
                                }}
                            >
                                {hasScale ? T._('Edit scale', 'Admin') : T._('Set scale', 'Admin')}
                            </BasicButton>
                        </Row>
                    </>
                )}
            </Inspector>
            <Row $horizontal $gap='8px'>
                <GhostButton
                    type='button'
                    $size='small'
                    disabled={busy || Boolean(edit) || Boolean(scaleEdit) || !side.entityReferenceKey || !revisionNumber}
                    onClick={() => fileInputRef.current?.click()}
                >
                    {T._('Replace image', 'Admin')}
                </GhostButton>
                <HiddenFileInput ref={fileInputRef} type='file' accept='image/*' onChange={onFileSelected} />
                {working && <Hint role='status'>{T._('Saving…', 'Admin')}</Hint>}
            </Row>
            <Hint>{T._('Changes are saved to the draft.', 'Admin')}</Hint>
            {!canEdit && image?.url && <Hint>{T._('Image dimensions are required before print areas can be edited.', 'Admin')}</Hint>}
            {localError && <ErrorText role='alert'>{localError}</ErrorText>}
        </Card>
    );
};
